import * as React from 'react'
import { inject, observer } from 'mobx-react'
import { IBaseCategory } from '@getgreenline/homi-shared'
import { message } from 'antd'
import { WeedmapApi, WeedmapModels } from '@getgreenline/weedmaps'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { IntegrationCategories } from '../IntegrationComponents/IntegrationCategories'
import { parseErrorMsg } from '../../../../utilities/helpers'
import { Track } from '../../../../Track'
import {
  IntegrationStore,
  IntegrationCategory,
  Integrators,
  IIntegrationCategoryItem,
} from '../IntegrationStore'
import { Routes } from '../../../../constants/Routes'
import { ICategoryContract } from '@getgreenline/weedmaps/build/models'

interface Props {
  integrationStore: IntegrationStore<Integrators.WEEDMAP>
}

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
}

interface State {
  isLoading: boolean
  treeCategories: WeedmapModels.INestedWeedMapCategoryContract[]
}

@inject('currentCompanyStore')
@observer
export class WeedmapsCategories extends React.Component<Props & InjectedProps, State> {
  weedmapApi = new WeedmapApi.WeedmapCategoryApi()

  availableSubCategories = new Map<string, IIntegrationCategoryItem<Integrators.WEEDMAP>[]>()

  categoryMappings = new Map<string, string>()

  constructor(props: Props) {
    super(props)
    this.state = {
      isLoading: false,
      treeCategories: [],
    }
  }

  componentDidMount = async () => {
    this.setState({
      isLoading: true,
    })

    await this.props.currentCompanyStore!.productStore!.getCategories()

    // Get tree structure from of weedmap categories
    const treeCategories = await this.weedmapApi.getTreeCategories()
    this.setState({
      treeCategories,
    })

    await this.setCategoryMappings()
    const { flattenedCategories } = this.props.currentCompanyStore!.productStore!
    await this.setIntegrationCategories(flattenedCategories)

    this.setState({
      isLoading: false,
    })
  }

  getAvailableRootCategories = async () => {
    const weedmapRootCategories = await this.weedmapApi.getRootCategories()

    return weedmapRootCategories.map((category): IIntegrationCategoryItem<Integrators.WEEDMAP> => {
      return {
        id: category.id,
        name: category.name,
      }
    })
  }

  setIntegrationCategories = async (greenlineCategories: IBaseCategory[]) => {
    const mappedCategoryItems = new Map<string, IntegrationCategory<Integrators.WEEDMAP>>()

    const availableRootCategories = await this.getAvailableRootCategories()

    for (const category of greenlineCategories) {
      const integrationCategory = this.getWeedmapCategory(category) || null

      const categoryItem = new IntegrationCategory(category, integrationCategory)

      if (category.parentCategoryId) {
        const parentCategoryItem = mappedCategoryItems.get(category.parentCategoryId)

        categoryItem.setIsDisabled(!parentCategoryItem?.integrationCategory)

        const WMCategoryId = this.categoryMappings.get(category.parentCategoryId)

        const availableCategories = WMCategoryId
          ? this.availableSubCategories.get(WMCategoryId) || []
          : []

        categoryItem.setAvailableCategories(availableCategories)
      } else {
        categoryItem.setAvailableCategories(availableRootCategories)
      }

      mappedCategoryItems.set(category.id, categoryItem)
    }

    this.props.integrationStore.setIntegrationCategories([...mappedCategoryItems.values()])
  }

  setCategoryMappings = async (): Promise<void> => {
    const company = this.props.currentCompanyStore?.company
    const authToken = window.localStorage.getItem('authToken')

    const categoryMappings = new Map<string, string>()
    if (authToken !== null && company !== undefined) {
      const mappingResponse = await WeedmapApi.WeedmapCategoryApi.getCategoryMapping(
        company.id,
        authToken,
      )
      mappingResponse.forEach((mapping) => {
        categoryMappings.set(mapping.GRCategoryId, mapping.WMCategoryId)
        this.setAvailableSubCategories(mapping.WMCategoryId)
      })
    }

    this.categoryMappings = categoryMappings
  }

  getWeedmapCategory = (
    greenlineCategory: IBaseCategory,
  ): IIntegrationCategoryItem<Integrators.WEEDMAP> | undefined => {
    const { treeCategories } = this.state
    const { categoryMappings } = this

    if (greenlineCategory.parentCategoryId) {
      // find from its parent category's sub-category
      const parentWeedmapCategoryId = categoryMappings.get(greenlineCategory.parentCategoryId)
      const flatSubCategories = parentWeedmapCategoryId
        ? this.availableSubCategories.get(parentWeedmapCategoryId) || []
        : []

      return flatSubCategories.find(
        (category) => category.id === categoryMappings.get(greenlineCategory.id),
      )
    }

    // find from root categories
    const WMCategoryId = categoryMappings.get(greenlineCategory.id)
    return WMCategoryId
      ? treeCategories.find((category) => category.id === WMCategoryId)
      : undefined
  }

  getSubCategoryAsList = (
    WMCategoryId: string,
    tree: WeedmapModels.INestedWeedMapCategoryContract[],
  ) => {
    let category: WeedmapModels.INestedWeedMapCategoryContract | undefined

    tree.forEach((ele: WeedmapModels.INestedWeedMapCategoryContract) => {
      if (ele.id === WMCategoryId) {
        category = ele
      }
    })
    return category === undefined ? [] : this.recursionTree(category)
  }

  get categoriesDescription() {
    const { integrationStore, currentCompanyStore } = this.props
    const { isLoading } = this.state

    if (!isLoading) {
      return (
        <div className='alert alert-warning'>
          {integrationStore.hasNoApiKey && !isLoading ? (
            <a
              href={Routes.DASHBOARD_INTEGRATIONS(currentCompanyStore!.company!.id)}
              target='_blank'
              rel='noreferrer'
            >
              Request your API keys from Weedmaps
            </a>
          ) : (
            'Parent category must be selected in order to select subcategories.'
          )}
        </div>
      )
    }
  }

  recursionTree = (node: WeedmapModels.INestedWeedMapCategoryContract) => {
    const obj = {
      id: node.id,
      name: node.name,
    }
    let result = [obj]
    Object.keys(node.subCategories).forEach((WMCategoryId) => {
      result = [...result, ...this.recursionTree(node.subCategories[WMCategoryId])]
    })
    return result
  }

  setAvailableSubCategories = (categoryId: string) => {
    const flatSubCategories = this.getSubCategoryAsList(categoryId, this.state.treeCategories).map(
      (subCategory) => ({
        id: subCategory.id,
        name: subCategory.name,
      }),
    )

    this.availableSubCategories.set(categoryId, flatSubCategories)
  }

  onIntegrationCategorySelect = async (
    index: number,
    categoryItem: IntegrationCategory<Integrators.WEEDMAP>,
    categoryId: string | undefined,
  ) => {
    let loopIndex = index
    let nextParentId

    const selectedIntegrationCategory =
      categoryId === undefined
        ? null
        : categoryItem.mappedAvailableCategories.get(categoryId) || null

    // Convert tree structure weedmap catgeory to flat list of one parent node
    if (categoryId) {
      this.setAvailableSubCategories(categoryId)
    }

    do {
      this.props.integrationStore.integrationCategories[loopIndex].setIntegrationCategory(
        selectedIntegrationCategory,
      )

      loopIndex++

      if (loopIndex >= this.props.integrationStore.integrationCategories.length) {
        break
      }

      const nextCategoryItem = this.props.integrationStore.integrationCategories[loopIndex]

      nextParentId = nextCategoryItem.greenlineCategory.parentCategoryId

      if (nextParentId && nextParentId === categoryItem.greenlineCategory.id) {
        nextCategoryItem.setIsDisabled(!selectedIntegrationCategory)
        const availableSubCategories = categoryId
          ? this.availableSubCategories.get(categoryId) || []
          : []
        nextCategoryItem.setAvailableCategories(availableSubCategories)
      }
    } while (nextParentId && nextParentId === categoryItem.greenlineCategory.id)
  }

  updateCategories = async () => {
    try {
      const { updatableCatagories } = this.props.integrationStore

      const company = this.props.currentCompanyStore?.company
      const authToken = window.localStorage.getItem('authToken')
      const deleteMappings: ICategoryContract[] = []
      const upsertMappings: ICategoryContract[] = []
      if (authToken !== null && company !== undefined) {
        for (const updatableCategory of updatableCatagories) {
          if (updatableCategory.updatedIntegrationCategory === null) {
            // delete mapping
            if (updatableCategory.integrationCategory) {
              deleteMappings.push({
                GRCategoryId: updatableCategory.greenlineCategory.id,
                WMCategoryId: updatableCategory.integrationCategory.id,
              })
            }
            // if integrationCategory was null, it wouldn't have been in updatableCatagories
          } else {
            // upsert mapping
            if (updatableCategory.updatedIntegrationCategory) {
              upsertMappings.push({
                GRCategoryId: updatableCategory.greenlineCategory.id,
                WMCategoryId: updatableCategory.updatedIntegrationCategory.id,
              })
            }
          }
        }
        if (deleteMappings.length > 0) {
          await WeedmapApi.WeedmapCategoryApi.deleteCategoryMappings(
            company.id,
            deleteMappings,
            authToken,
          )
        }

        if (upsertMappings.length > 0) {
          await WeedmapApi.WeedmapCategoryApi.upsertCategoryMappings(
            company.id,
            upsertMappings,
            authToken,
          )
        }
      } else {
        throw new Error(`company or auth token is missing`)
      }

      updatableCatagories.forEach((category) => {
        category.syncIntegrationCategory()
      })

      Track.track('dashboard:integrations_weedmapsCategoryUpdate')

      message.success('Categories successfully updated')
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while updating categories')
    }
  }

  render() {
    const { integrationStore } = this.props
    const { isLoading } = this.state
    return (
      <IntegrationCategories
        integrationStore={integrationStore}
        onIntegrationCategorySelect={this.onIntegrationCategorySelect}
        onUpdate={this.updateCategories}
        isLoading={isLoading}
        description={this.categoriesDescription}
      />
    )
  }
}
